import { z } from 'zod';
import { addressLocationSchema, createModel, QueryMany } from '../../common';
import { locationTrackingActivitySchema } from './location-tracking.activity.model';
import { locationTrackingDeviceSchema } from './location-tracking.device.model';
import { floatNumSchema, integerSchema } from '../../validation';
import { locationTrackingEventTypeSchema } from './location-tracking.event.model';
import { locationTrackingUserSchema } from './location-tracking.user.model';
import { locationTrackingOrderSchema } from './location-tracking.order.model';
import { locationTrackingVehicleSchema } from './location-tracking.vehicle.model';

/** `LocationTracking db model` */
export const LocationTrackingSchema = createModel({
  /** the activity location. */
  location: addressLocationSchema,
  /** the user activity type such as `in_vehicle`, `walking`, etc  */
  activity: locationTrackingActivitySchema,
  /** the device in which the activity was logged */
  device: locationTrackingDeviceSchema,
  /** the speed of the device in KM */
  speed: integerSchema,
  /**
   * heading refers to the direction in which the device is moving, expressed in degrees.
   *
   * A heading of `0°` corresponds to `north`, `90°` to `east`, `180°` to `south`, and `270°` to `west`.
   */
  heading: floatNumSchema,
  /**
   * Event responsible for triggering the location:
   * - `motionchange`
   * - `providerchange`
   * - `geofence`
   * - `heartbeat`
   */
  event: locationTrackingEventTypeSchema,
  /**
   * The motion-state when recorded
   */
  isMoving: z.boolean(),

  /** the tracked user that is being tracked */
  user: locationTrackingUserSchema,
  /** the current active order.
   *
   * If empty, then it means one of the following things:
   *  - the location was tracked before an order has been assigned to a driver.
   *  - the current user is NOT a driver.
   */
  order: locationTrackingOrderSchema.optional(),

  /** the current user's vehicle that is being tracked.
   *
   * This will be empty for one of the following reasons:
   * - The `user` being tracked is NOT a driver, and there does not have a vehicle to add.
   * - The driver has not being a vehicle yet.
   */
  vehicle: locationTrackingVehicleSchema.optional(),

  /** any other data received from the tracking activity */
  other: z.any().optional(),
});

/** `LocationTracking db model` */
export type LocationTracking = z.infer<typeof LocationTrackingSchema.model>;
export type LocationTrackingData = z.infer<typeof LocationTrackingSchema.data>;
export type LocationTrackingPartial = z.infer<typeof LocationTrackingSchema.partial>;

/** `LocationTracking` model query */
export type LocationTrackingQuery = QueryMany<LocationTracking>;
